import { useEffect, useState } from "react";
import Dropzone from "react-dropzone";

import {
  Attach16Regular,
  AttachText20Regular,
  Delete16Regular,
  Send16Regular
} from "@fluentui/react-icons";
import { useGlobalContext } from "app/store/GlobalContext";
import { useInboxReplyContext } from "app/store/InboxReplyContext";
import { usePermission } from "hooks/use-permissions";
import { Alert } from "components/alert/Alert";
import { Button } from "components/button/Button";
import { AppButton } from "components/appButton/AppButton";
import { UPLOAD_FILES_LIMIT } from "components/fileInput/fileInputConsts";
import { Icon } from "components/icon/Icon";
import { Input } from "components/input/Input";
import { Loader } from "components/loader/Loader";
import Modal from "components/modal/Modal";
import UnsavedChangesModal from "components/shared/unsavedChangesModal/UnsavedChangesModal";
import TextEditor from "components/textEditor/TextEditor";
import { Tooltip } from "components/tooltip/Tooltip";
import { filterDuplicateFiles } from "helpers/FilterDuplicateFiles";
import { useDebouncedContentValidation } from "hooks/use-debounced-content-validation";
import { Attachment, Subscriber } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import { Thread, ThreadCategory } from "models/inbox/inbox";
import { useLocation } from "react-router";
import { useRootStore } from "store";
import { MessageType } from "store/slices/inboxSlice";
import DocumentArchiveModal from "views/documents/containers/DocumentArchiveModal";
import {
  NODE_TYPE_FOLDER,
  NODE_TYPE_ROOT
} from "views/documents/state/documentArchiveOptions";
import CategorySelector from "views/inbox/components/Inbox/main/threadPreview/CategorySelector";
import RecipientsSelectorWithAbsenceMessage from "views/inbox/components/recipientsSelectorWithAbsenceMessage/RecipientsSelectorWithAbsenceMessage";
import ContentSizeAlert from "../ContentSizeAlert";

import "../CreateThread.scss";

type Props = {
  defaultRecipients: Subscriber[];
  isSending: boolean;
  onSend: (
    title: string,
    body: string,
    recipients: string[],
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[],
    messageType?: string,
    reportStart?: string,
    reportEnd?: string,
    category?: ThreadCategory
  ) => void;
  onDiscardClicked: () => void;
  showSubjectLine?: boolean;
  thread?: Thread;
};

export default function CreateThreadDesktop({
  defaultRecipients,
  showSubjectLine,
  isSending,
  thread,
  onSend,
  onDiscardClicked
}: Props) {
  const { editingState, setEditingState, redirectingState } =
    useGlobalContext();
  const currentLocation = useLocation();
  const { formState, updateFormState, markDirty, clearDirty } =
    useInboxReplyContext();

  const isInternalUser = usePermission("isInternal");
  const { user, archive } = useRootStore((state) => state);
  const { workingCustomer: customer } = useRootStore(
    (state) => state.customers
  );
  const { customerNumber, internalUsers, externalUsers } = customer;
  const {
    getArchiveFolderFiles,
    setSelectedArchiveNodeId,
    toggleArchiveTreeNodeId
  } = archive;

  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [hasTooManyFiles, setHasTooManyFiles] = useState(false);

  const { addNotification } = useRootStore((state) => state.toastNotifications);

  // Extract form state from context
  const {
    recipients,
    title,
    text,
    attachments,
    computerAttachments,
    selectedCategory,
    validation
  } = formState;

  // Debounced content validation hook
  useDebouncedContentValidation({
    text,
    validation,
    recipients,
    showSubjectLine,
    updateFormState,
    onValidationChange: undefined
  });

  useEffect(() => {
    return () => {
      setEditingState({ isEditing: false, location: null });
    };
  }, [setEditingState]);

  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const handleOnRecipientsChange = (threadRecipients: any[]) => {
    setEditingState({ isEditing: true, location: "conversation" });
    updateFormState({ recipients: threadRecipients });
    markDirty();
  };

  const handleOnSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setEditingState({ isEditing: true, location: "conversation" });
    markDirty();

    const newTitleValidation =
      showSubjectLine || (value.length > 0 && value.length <= 78)
        ? "VALID"
        : "INVALID";
    updateFormState({
      title: value,
      validation: { ...validation, title: newTitleValidation }
    });
  };

  const onTextChange = (value: string) => {
    markDirty();
    setEditingState({ isEditing: true, location: "conversation" });

    // Update text immediately for responsive UI
    updateFormState({ text: value });
  };

  const handleSendMessage = () => {
    if (!recipients.length) {
      setShowRecipientModal(!showRecipientModal);
      return;
    }
    if (isValid()) {
      onSend(
        title,
        text,
        recipients.map((recipient) => recipient.id),
        attachments,
        computerAttachments,
        MessageType.Normal,
        undefined,
        undefined,
        selectedCategory
      );
      clearDirty();
    }
  };

  const handleTreeNodeClicked = (node: Attachment) => {
    if (node.type === NODE_TYPE_FOLDER || node.type === NODE_TYPE_ROOT) {
      toggleArchiveTreeNodeId(node.id);
      setSelectedArchiveNodeId(node.id);
      getArchiveFolderFiles(customerNumber, node.id);
    } else {
      if (attachments.find((attachment) => attachment.id === node.id)) {
        addNotification(
          "warning",
          `Följande fil är redan uppladdad: ${node.name}`
        );
        return;
      }

      setEditingState({ isEditing: true, location: "conversation" });
      updateFormState({ attachments: [...attachments, node] });
      markDirty();
      setShowArchiveModal(false);
    }
  };

  const handleOnRemove = (item: Attachment) => {
    const updatedAttachments = attachments.filter((attachment) => {
      return attachment.id !== item.id;
    });
    updateFormState({ attachments: updatedAttachments });
    markDirty();
    setShowArchiveModal(false);
  };

  const handleOnComputerRemove = (index: number) => {
    const updatedAttachments = computerAttachments.filter(
      (_, i) => i !== index
    );
    updateFormState({ computerAttachments: updatedAttachments });
    markDirty();
    setHasTooManyFiles(false);
  };

  const addNewFilesAndCheckLimit = (addFiles: File[]) => {
    setEditingState({ isEditing: true, location: "conversation" });
    const newFilesToAdd = filterDuplicateFiles(
      addFiles,
      computerAttachments.map((attachment) => attachment.file)
    );

    const existingFiles = [
      ...computerAttachments,
      ...newFilesToAdd.map((file) => ({
        file: file,
        meta: {
          fileName: file.name,
          alternativeFileName: undefined
        }
      }))
    ];

    setHasTooManyFiles(existingFiles.length > UPLOAD_FILES_LIMIT);
    updateFormState({
      computerAttachments: existingFiles.slice(0, UPLOAD_FILES_LIMIT)
    });
    markDirty();
  };

  const isValid = () => {
    return (
      recipients.length > 0 &&
      (!showSubjectLine || validation.title === "VALID") &&
      validation.body === "VALID" &&
      validation.contentSize === "VALID"
    );
  };

  const userInfoId = user.userInfo ? user.userInfo.id : "";
  const SSC = "SSC";

  const availableInternalUsers = (internalUsers || []).filter(
    (availableInternalUser) => availableInternalUser.organization !== SSC
  );

  const availableRecipients = [
    ...availableInternalUsers,
    ...externalUsers
  ].filter((availableUser) => availableUser.id !== userInfoId);

  const selectedRecipients = recipients.filter(
    (recipient) => recipient.id !== userInfoId
  );

  return (
    <div className="create-wrapper">
      {!user.userInfo && <Loader />}
      {showRecipientModal && (
        <Modal
          header={<h1>Mottagare saknas</h1>}
          onDismiss={() => setShowRecipientModal(false)}
          modalTitle="recipient missing"
        >
          <p>Vänligen lägg till en mottagare för att skicka meddelandet.</p>
          <Button onClick={() => setShowRecipientModal(!showRecipientModal)}>
            Ok
          </Button>
        </Modal>
      )}

      {showArchiveModal && (
        <DocumentArchiveModal
          header={<h1 className="mt-0">Bifoga fil</h1>}
          onSelectFile={handleTreeNodeClicked}
          onDismiss={() => setShowArchiveModal(false)}
        />
      )}

      {isInternalUser && !thread && (
        <CategorySelector
          currentCategory={selectedCategory}
          onCategoryChange={(category) => {
            updateFormState({ selectedCategory: category });
            markDirty();
          }}
          mobile={false}
          disabled={false}
        />
      )}

      <div className={showSubjectLine ? "pb-4" : ""}>
        <label className="mb-3">Lägg till mottagare</label>

        <RecipientsSelectorWithAbsenceMessage
          selectedRecipients={selectedRecipients}
          availableRecipients={availableRecipients}
          onChange={handleOnRecipientsChange}
        />

        <div className="mt-4 text-editor-input-wrapper">
          <label className="mb-3">Ämne</label>
          <Input
            maxLength={78}
            onChange={handleOnSubjectChange}
            type="text"
            placeholder="Skriv rubrik"
            value={title}
            disabled={!!thread}
          />
        </div>

        <div className="mt-4">
          <label className="mb-3">Meddelande</label>
          <TextEditor
            value={text}
            placeholder="Skriv meddelande"
            onChange={onTextChange}
          />
          <ContentSizeAlert isInvalid={validation.contentSize === "INVALID"} />
        </div>

        <div className="mt-4">
          {attachments.length + computerAttachments.length > 0 && (
            <label className="mb-3">Bifogade filer</label>
          )}

          {attachments.map((attachment) => {
            return (
              <div
                key={attachment.id}
                className="cursor-pointer"
                onClick={() => handleOnRemove(attachment)}
              >
                <Icon name="times" className="text-ludvig-red mr-3 mb-2" />
                {attachment.name}
              </div>
            );
          })}

          {computerAttachments.map((attachment, i) => {
            return (
              <div
                key={`${attachment.file.name}-${attachment.file.type}-${attachment.file.lastModified}`}
                className="cursor-pointer"
                onClick={() => handleOnComputerRemove(i)}
              >
                <Icon name="times" className="text-ludvig-red mr-3 mb-2" />
                {attachment.file.name}
              </div>
            );
          })}

          {hasTooManyFiles && (
            <Alert
              className="mt-2"
              variant="warning"
              onDismiss={() => setHasTooManyFiles(false)}
            >
              <Icon name="exclamation-circle" className="mr-2" />
              <span>Du kan ladda upp max femton filer åt gången.</span>
            </Alert>
          )}
        </div>

        <div className="mt-3 d-flex flex-wrap">
          <Tooltip content={<p className="m-0">Skicka</p>}>
            <AppButton
              variant="filled"
              color="brand"
              size="sm"
              shape="circular"
              disabled={!isValid() || isSending}
              className="text-nowrap mr-3 mt-3"
              onClick={handleSendMessage}
              aria-label="Skicka"
              icon={<Send16Regular className="app-button-icon" />}
              iconPosition="after"
            >
              Skicka
            </AppButton>
          </Tooltip>

          {isInternalUser && (
            <Tooltip
              content={<p className="m-0">Bifoga fil från Dokumentarkivet</p>}
            >
              <AppButton
                variant="outline"
                size="sm"
                shape="circular"
                disabled={isSending}
                onClick={() => setShowArchiveModal(true)}
                className="text-nowrap mr-3 mt-3"
                icon={
                  <AttachText20Regular
                    className="app-button-icon"
                    style={{ width: "17px", height: "17px" }}
                  />
                }
                iconPosition="after"
                aria-label="Bifoga fil från Dokumentarkivet"
              >
                Bifoga fil från Dokumentarkivet
              </AppButton>
            </Tooltip>
          )}

          <Dropzone onDrop={addNewFilesAndCheckLimit}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <Tooltip
                  content={
                    <p className="m-0">
                      Bifoga fil från dator, mobil eller surfplatta
                    </p>
                  }
                >
                  <AppButton
                    variant="outline"
                    size="sm"
                    disabled={isSending}
                    shape="circular"
                    className="text-nowrap mr-3 mt-3"
                    onClick={() => {}}
                    aria-label="Bifoga fil från dator, mobil eller surfplatta"
                    icon={<Attach16Regular className="app-button-icon" />}
                    iconPosition="after"
                  >
                    Bifoga fil
                  </AppButton>
                </Tooltip>
              </div>
            )}
          </Dropzone>

          <Tooltip content={<p className="m-0">Släng utkast</p>}>
            <AppButton
              variant="outline"
              size="sm"
              shape="circular"
              onClick={onDiscardClicked}
              className="text-nowrap mr-3 mt-3"
              aria-label="Släng utkast"
              icon={<Delete16Regular className="app-button-icon" />}
              iconPosition="after"
            >
              Släng utkast
            </AppButton>
          </Tooltip>
        </div>
      </div>
      {editingState.isEditing &&
        editingState.location === "conversation" &&
        redirectingState.isRedirecting &&
        redirectingState.url !== currentLocation.pathname && (
          <UnsavedChangesModal text="Du har ett osparat meddelande. Är du säker på att du vill lämna sidan?" />
        )}
      {!showSubjectLine && <hr className="hr-divider"></hr>}
    </div>
  );
}
